import React, { createContext, useContext, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const OwnerCalendarContext = createContext();

export const useOwnerCalendar = () => {
    const context = useContext(OwnerCalendarContext);
    if (!context) {
        throw new Error('useOwnerCalendar must be used within OwnerCalendarProvider');
    }
    return context;
};

export const OwnerCalendarProvider = ({ children }) => {
    // Navigation state
    const [currentWeekStart, setCurrentWeekStart] = useState(() => dayjs().startOf('isoWeek'));

    // Selection state
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [selectedDogId, setSelectedDogId] = useState(null);
    const [selectedServiceId, setSelectedServiceId] = useState(null);

    // Booking mode state
    const [bookingMode, setBookingMode] = useState('single'); // 'single' or 'multiple'
    const [selectedSlots, setSelectedSlots] = useState([]); // Array of slot strings for multiple booking

    // UI state - Modals
    const [additionalInfoModalOpen, setAdditionalInfoModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [trainingToDelete, setTrainingToDelete] = useState(null);

    // School settings
    const [schoolSettings, setSchoolSettings] = useState(null);

    // Loading states
    const [isBooking, setIsBooking] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);

    // Navigation functions
    const goToPreviousWeek = useCallback(() => {
        setCurrentWeekStart(prev => prev.subtract(1, 'week'));
        setSelectedSlots([]); // Clear selections when changing week
    }, []);

    const goToNextWeek = useCallback(() => {
        setCurrentWeekStart(prev => prev.add(1, 'week'));
        setSelectedSlots([]); // Clear selections when changing week
    }, []);

    const goToWeek = useCallback((date) => {
        setCurrentWeekStart(dayjs(date).startOf('isoWeek'));
        setSelectedSlots([]); // Clear selections when changing week
    }, []);

    const goToToday = useCallback(() => {
        setCurrentWeekStart(dayjs().startOf('isoWeek'));
        setSelectedSlots([]); // Clear selections
    }, []);

    // Selection functions
    const selectTrainer = useCallback((trainer) => {
        setSelectedTrainer(trainer);
        setSelectedSlots([]); // Clear slot selections when trainer changes
    }, []);

    const selectDog = useCallback((dogId) => {
        setSelectedDogId(dogId);
        setSelectedSlots([]); // Clear slot selections when dog changes
    }, []);

    const selectService = useCallback((serviceId) => {
        setSelectedServiceId(serviceId);
    }, []);

    // Booking mode functions
    const enableMultipleBookingMode = useCallback(() => {
        setBookingMode('multiple');
        setSelectedSlots([]);
    }, []);

    const disableMultipleBookingMode = useCallback(() => {
        setBookingMode('single');
        setSelectedSlots([]);
    }, []);

    const toggleBookingMode = useCallback(() => {
        setBookingMode(prev => {
            const newMode = prev === 'single' ? 'multiple' : 'single';
            if (newMode === 'single') {
                setSelectedSlots([]);
            }
            return newMode;
        });
    }, []);

    // Slot selection functions for multiple booking
    const toggleSlotSelection = useCallback((slotTime) => {
        const slotStr = typeof slotTime === 'string'
            ? slotTime
            : slotTime.format('YYYY-MM-DDTHH:mm:ss');

        setSelectedSlots(prev => {
            if (prev.includes(slotStr)) {
                return prev.filter(s => s !== slotStr);
            } else {
                return [...prev, slotStr];
            }
        });
    }, []);

    const selectAllSlotsForDay = useCallback((daySlots) => {
        setSelectedSlots(prev => {
            const newSlots = [...prev];
            daySlots.forEach(slot => {
                const slotStr = typeof slot === 'string'
                    ? slot
                    : slot.format('YYYY-MM-DDTHH:mm:ss');
                if (!newSlots.includes(slotStr)) {
                    newSlots.push(slotStr);
                }
            });
            return newSlots;
        });
    }, []);

    const deselectAllSlotsForDay = useCallback((daySlots) => {
        setSelectedSlots(prev => {
            const slotsToRemove = daySlots.map(slot =>
                typeof slot === 'string'
                    ? slot
                    : slot.format('YYYY-MM-DDTHH:mm:ss')
            );
            return prev.filter(s => !slotsToRemove.includes(s));
        });
    }, []);

    const clearSelectedSlots = useCallback(() => {
        setSelectedSlots([]);
    }, []);

    // Check if a slot is selected
    const isSlotSelected = useCallback((slotTime) => {
        const slotStr = typeof slotTime === 'string'
            ? slotTime
            : slotTime.format('YYYY-MM-DDTHH:mm:ss');
        return selectedSlots.includes(slotStr);
    }, [selectedSlots]);

    // Modal functions
    const openAdditionalInfoModal = useCallback(() => {
        setAdditionalInfoModalOpen(true);
    }, []);

    const closeAdditionalInfoModal = useCallback(() => {
        setAdditionalInfoModalOpen(false);
    }, []);

    const openDeleteModal = useCallback((training) => {
        setTrainingToDelete(training);
        setDeleteModalOpen(true);
    }, []);

    const closeDeleteModal = useCallback(() => {
        setDeleteModalOpen(false);
        setTrainingToDelete(null);
    }, []);

    // Booking validation helpers
    const canBookSlot = useCallback((slotTime) => {
        if (!schoolSettings) return true;

        const slot = dayjs(slotTime);
        const now = dayjs();

        // Check if slot is in the past
        if (slot.isBefore(now)) return false;

        // Check minimum booking days in advance
        if (schoolSettings.minBookingDaysInAdvance > 0) {
            const minBookingDate = now.add(schoolSettings.minBookingDaysInAdvance, 'day').startOf('day');
            if (slot.startOf('day').isBefore(minBookingDate)) {
                return false;
            }
        }

        // Check if month is unlocked
        if (schoolSettings.unlockedMonths && schoolSettings.unlockedMonths.length > 0) {
            const monthNames = [
                'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
                'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
            ];
            const slotMonth = monthNames[slot.month()];
            if (!schoolSettings.unlockedMonths.includes(slotMonth)) {
                return false;
            }
        }

        return true;
    }, [schoolSettings]);

    const canCancelTraining = useCallback((training) => {
        if (!schoolSettings || !training) return true;

        const trainingDate = dayjs(training.startTime);
        const now = dayjs();

        // Can always cancel unconfirmed trainings
        if (!training.confirmed) return true;

        // Check minimum days before cancel for confirmed trainings
        if (schoolSettings.minDaysBeforeCancel > 0) {
            const minCancelDate = trainingDate.subtract(schoolSettings.minDaysBeforeCancel, 'day').startOf('day');
            if (now.isAfter(minCancelDate)) {
                return false;
            }
        }

        return true;
    }, [schoolSettings]);

    // Get week label for display
    const getWeekLabel = useCallback(() => {
        const weekEnd = currentWeekStart.add(6, 'day');
        return `${currentWeekStart.format('DD MMM')} - ${weekEnd.format('DD MMM YYYY')}`;
    }, [currentWeekStart]);

    const value = {
        // State
        currentWeekStart,
        selectedTrainer,
        selectedDogId,
        selectedServiceId,
        bookingMode,
        selectedSlots,
        additionalInfoModalOpen,
        deleteModalOpen,
        trainingToDelete,
        schoolSettings,
        isBooking,
        isCanceling,

        // Setters
        setCurrentWeekStart,
        setSelectedTrainer,
        setSelectedDogId,
        setSelectedServiceId,
        setBookingMode,
        setSelectedSlots,
        setSchoolSettings,
        setIsBooking,
        setIsCanceling,

        // Navigation
        goToPreviousWeek,
        goToNextWeek,
        goToWeek,
        goToToday,
        getWeekLabel,

        // Selection
        selectTrainer,
        selectDog,
        selectService,

        // Booking mode
        enableMultipleBookingMode,
        disableMultipleBookingMode,
        toggleBookingMode,

        // Slot selection
        toggleSlotSelection,
        selectAllSlotsForDay,
        deselectAllSlotsForDay,
        clearSelectedSlots,
        isSlotSelected,

        // Modals
        openAdditionalInfoModal,
        closeAdditionalInfoModal,
        openDeleteModal,
        closeDeleteModal,

        // Validation
        canBookSlot,
        canCancelTraining,
    };

    return (
        <OwnerCalendarContext.Provider value={value}>
            {children}
        </OwnerCalendarContext.Provider>
    );
};